export type EventResponseChoice = 'going' | 'interested';

export type EventResponseStatus = {
  my_response: EventResponseChoice | null;
  going_count: number;
  interested_count: number;
  is_full: boolean;
};

type EventDetailResponse = {
  event: {
    my_response: EventResponseChoice | null;
    going_count: number;
    interested_count: number;
    is_full: boolean;
  };
};

type ApiErrorPayload = {
  error?: string | { code?: string; message?: string };
  message?: string;
};

export class EventResponseApiError extends Error {
  readonly status: number;
  readonly payload: ApiErrorPayload | null;

  constructor(status: number, payload: ApiErrorPayload | null) {
    const errorMessage = typeof payload?.error === 'object' ? payload.error.message : null;
    super(errorMessage ?? payload?.message ?? `Event response failed with status ${status}`);
    this.name = 'EventResponseApiError';
    this.status = status;
    this.payload = payload;
  }
}

let csrfToken: string | null = null;

async function parseJson(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.includes('application/json') ? response.json() : null;
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;

  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const body = (await parseJson(response)) as { csrf_token?: string } | null;

  if (!response.ok || !body?.csrf_token) {
    throw new EventResponseApiError(response.status, body as ApiErrorPayload | null);
  }

  csrfToken = body.csrf_token;
  return csrfToken;
}

async function eventFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');

  if (options.body !== undefined && options.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-CSRF-TOKEN', await ensureCsrf());
  }

  const response = await fetch(url, {
    ...options,
    method,
    credentials: 'same-origin',
    headers,
  });
  const body = await parseJson(response);

  if (!response.ok) {
    throw new EventResponseApiError(response.status, body as ApiErrorPayload | null);
  }

  return body as T;
}

export async function fetchEventResponseStatus(eventId: string, signal?: AbortSignal): Promise<EventResponseStatus> {
  const response = await eventFetch<EventDetailResponse>(`/api/v1/events/${encodeURIComponent(eventId)}`, { signal });
  return {
    my_response: response.event.my_response,
    going_count: response.event.going_count,
    interested_count: response.event.interested_count,
    is_full: response.event.is_full,
  };
}

export function respondToEvent(eventId: string, responseType: EventResponseChoice): Promise<EventResponseStatus> {
  return eventFetch(`/api/v1/events/${encodeURIComponent(eventId)}/respond`, {
    method: 'POST',
    body: JSON.stringify({ response_type: responseType }),
  });
}

export function removeEventResponse(eventId: string): Promise<EventResponseStatus> {
  return eventFetch(`/api/v1/events/${encodeURIComponent(eventId)}/respond`, { method: 'DELETE' });
}
